import { array as codecArray } from 'purify-ts/Codec';
import { makeResponses } from '../response_factory';
import { utils as coreUtils, paginated, requestParams } from '@codegrade/api-core';
import { ConfirmOTPLoginData, CreateOTPLoginData, OTPLoginableUser, OTPLoginMultiResponse, OTPLoginResponse, OTPLoginSingleResponse, SelectOTPLoginData, } from '@codegrade/api-models';
import { parsers } from '@codegrade/utils';
const makeResult = coreUtils.makeResult;
// eslint-disable-next-line @typescript-eslint/no-unused-vars,unused-imports/no-unused-vars
const parseJsonResponse = /* #__PURE__ */ parsers.parseJsonResponse;
export function otpLogin(config) {
    const confirmResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(
        /* #__PURE__ */ parseJsonResponse(
        /* #__PURE__ */ parsers.taggedUnion('type', [
            ['login', OTPLoginSingleResponse],
            ['select', OTPLoginMultiResponse],
        ]))),
    });
    const createResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(/* #__PURE__ */ parseJsonResponse(OTPLoginResponse)),
    });
    const listResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ paginated.makeResult(
        /* #__PURE__ */ parseJsonResponse(/* #__PURE__ */ codecArray(OTPLoginableUser))),
    });
    const selectResponse = /* #__PURE__ */ makeResponses({
        200: /* #__PURE__ */ makeResult(/* #__PURE__ */ parseJsonResponse(OTPLoginSingleResponse)),
    });
    return Object.freeze({
        /**
         * Verify the OTP code.
         *
         * Verifies the code sent to the user's email. If exactly one active user matches, returns a login response with an access token and consumes the session. If multiple users match, returns `{"type": "select"}` — use `otp_login.list` and `otp_login.select` to choose. If no users match, returns 404 (same as wrong code, anti-enumeration). Allows up to 5 attempts before the session is locked.
         *
         * @param arg The arguments to the function.
         * @param arg.otpId The OTP session identifier.
         * @param arg.requestBody The body of the request.
         * @returns Either a LoginResponse or a selection marker.
         */
        confirm: async ({ otpId, requestBody, }) => {
            const result = await requestParams(config, 'POST', `/api/v1/otp_logins/${otpId}/confirm`, confirmResponse.v, JSON.stringify(ConfirmOTPLoginData.encode(requestBody)));
            return result;
        },
        /**
         * Create an OTP login session and send a verification code.
         *
         * Starts the passwordless login flow. A one-time code is sent to the given email address. The caller then verifies it via `otp_login.confirm`:
         *
         * - Single user match → login response with access token. - Multiple users → `{"type": "select"}`; use `otp_login.list` to enumerate and `otp_login.select` to choose.
         *
         * The session is IP-bound and requires a proof-of-work solution. Always returns success regardless of whether matching users exist to prevent email enumeration.
         *
         * @param arg The arguments to the function.
         * @param arg.requestBody The body of the request.
         * @returns An OTP session identifier.
         */
        create: async ({ requestBody, }) => {
            const result = await requestParams(config, 'POST', `/api/v1/otp_logins`, createResponse.v, JSON.stringify(CreateOTPLoginData.encode(requestBody)));
            return result;
        },
        /**
         * List users matching this OTP session (paginated).
         *
         * Only available after `otp_login.confirm` returned `{"type": "select"}`. Requires the OTP code in the `Cg-Otp-Login-Code` header for authentication. Does not consume the session — can be called repeatedly until the session expires.
         *
         * Each user includes a `can_login` flag indicating whether they are allowed to log in directly (users managed by an external system like LTI/SSO may not be).
         *
         * @param arg The arguments to the function.
         * @param arg.otpId The OTP session identifier.
         * @param arg.cgOtpLoginCode The OTP login code.
         * @param arg.pageSize The size of a single page, maximum is 50.
         * @param arg.nextToken The next token, to retrieve a next page.
         * @returns Paginated list of matching users.
         */
        list: async ({ otpId, cgOtpLoginCode, pageSize = undefined, nextToken = undefined, }) => {
            const result = await requestParams(config, 'GET', `/api/v1/otp_logins/${otpId}/users/`, listResponse.v, undefined, {
                'page-size': pageSize,
                'next-token': nextToken,
            }, coreUtils.makeHeaders({
                'Cg-Otp-Login-Code': cgOtpLoginCode,
            }));
            return result;
        },
        /**
         * Select a user from the multi-user list and log in.
         *
         * Only available after `otp_login.confirm` returned `{"type": "select"}`. Requires the OTP code for re-authentication. Consumes the session — a second call will return 404.
         *
         * @param arg The arguments to the function.
         * @param arg.otpId The OTP session identifier.
         * @param arg.requestBody The body of the request.
         * @returns A login response for the selected user.
         */
        select: async ({ otpId, requestBody, }) => {
            const result = await requestParams(config, 'POST', `/api/v1/otp_logins/${otpId}/select`, selectResponse.v, JSON.stringify(SelectOTPLoginData.encode(requestBody)));
            return result;
        },
    });
}
